import React, { useMemo } from 'react';
import type { TabNavProps, TabNavItem, TabNavLinkComponentProps, TabNavMenuItemComponentProps } from './TabNav.types';
import {
  tabNavStyles,
  getActiveLinkStyles,
  getInactiveNativeLinkStyles,
  StyledRoot,
  StyledList,
  StyledItem,
  StyledLink,
  StyledIndicator,
  StyledMenuTrigger,
  StyledBadge,
  DEFAULT_MENU_ITEM_STYLES,
  DEFAULT_BADGE_STYLES,
  DEFAULT_SUBHEAD_STYLES,
  DEFAULT_DIVIDER_STYLES,
} from './TabNav.styles';
import { useTabNav } from './useTabNav';
import { useOverrides } from '@shared/utils/overrides';
import { createDisplayName } from '../../common/utils/createDisplayName';
import { forwardRef } from '@shared/utils/forwardRef';
import { useStyleConfig } from '@shared/joykit/packages/core/common/utils/styleConfig';
import { PopoverV2 } from '../PopoverV2';
import { Box } from '../Box';
import { Flex } from '../Flex';
import type { BaseBoxProps } from '../Box';
import OptionSelectDown from '@shared/joykit/packages/core/icons/components/OptionSelectDown';
import { Badge as JoyBadge } from '../Badge';
import { Portal } from '../Portal';
import { styled } from '@withjoy/joykit/theme';
import { pxToRem } from '@shared/joykit/packages/core/common/themes/shared';

/**
 * Default link component (native anchor)
 */
const DefaultLinkComponent: React.FC<TabNavLinkComponentProps> = React.forwardRef<HTMLAnchorElement, TabNavLinkComponentProps>(
  ({ href, children, 'aria-current': ariaCurrent, onClick, onKeyDown, tabIndex, ...rest }, ref) => {
    return (
      <a
        ref={ref}
        href={href}
        aria-current={ariaCurrent}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex}
        {...rest}
      >
        {children}
      </a>
    );
  },
);

DefaultLinkComponent.displayName = 'DefaultLinkComponent';

/**
 * Expand arrow icon for dropdown menus
 */
const ExpandArrow = styled(OptionSelectDown)<{ $isOpen: boolean }>`
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg) translateZ(0)' : 'none')};
  transition: transform 200ms ease-in-out;
`;

const TabNavMenuItemComponent: React.FC<TabNavMenuItemComponentProps> = ({ item, onClose, linkComponent: LinkComponent = DefaultLinkComponent, menuStyleOverrides }) => {
  const handleClick = () => {
    item.onClick?.();
    onClose();
  };

  const menuItemStyles = useMemo(() => ({ ...DEFAULT_MENU_ITEM_STYLES, ...menuStyleOverrides?.menuItem }), [menuStyleOverrides?.menuItem]);

  const badgeStyles = useMemo(() => ({ ...DEFAULT_BADGE_STYLES, ...menuStyleOverrides?.menuBadge }) as BaseBoxProps['__css'], [menuStyleOverrides?.menuBadge]);

  if (item.isSubhead) {
    return (
      <>
        {item.hasDivider && (
          <Box
            {...DEFAULT_DIVIDER_STYLES}
            {...menuStyleOverrides?.menuDivider}
          />
        )}
        <Box
          as="span"
          role="presentation"
          __css={DEFAULT_SUBHEAD_STYLES}
        >
          {item.label}
        </Box>
      </>
    );
  }

  const content = (
    <Flex
      alignItems="center"
      gap={2}
      flex={1}
    >
      {item.icon && <Box as="span">{item.icon}</Box>}
      <Box as="span">{item.label}</Box>
      {item.badgeText && (
        <Box
          as="span"
          __css={badgeStyles}
        >
          {item.badgeText}
        </Box>
      )}
    </Flex>
  );

  return (
    <>
      {item.hasDivider && (
        <Box
          {...DEFAULT_DIVIDER_STYLES}
          {...menuStyleOverrides?.menuDivider}
        />
      )}
      {item.href ? (
        <Box
          as={item.isExternal ? DefaultLinkComponent : (LinkComponent as React.ElementType)}
          href={item.href}
          role="menuitem"
          __css={menuItemStyles}
          color={item.labelColor}
          onClick={handleClick}
        >
          {content}
          {item.endAdornment}
        </Box>
      ) : (
        <Box
          as="button"
          type="button"
          role="menuitem"
          __css={menuItemStyles}
          color={item.labelColor}
          onClick={handleClick}
        >
          {content}
          {item.endAdornment}
        </Box>
      )}
    </>
  );
};

/**
 * TabNav - Navigation tabs component for page navigation.
 *
 * Use TabNav for actual page/route navigation with `aria-current="page"`.
 * For view/setting control without navigation, use SegmentedController from @withjoy/joykit.
 *
 * @example
 * // Basic usage - active state auto-detected from URL
 * <TabNav
 *   items={[
 *     { label: "Overview", href: "/overview" },
 *     { label: "Schedule", href: "/schedule" },
 *   ]}
 * />
 *
 * @example
 * // With React Router Link
 * import { Link } from 'react-router-dom';
 *
 * <TabNav
 *   items={[...]}
 *   linkComponent={Link}
 * />
 *
 * @example
 * // With dropdown menu
 * <TabNav
 *   items={[
 *     { label: "Overview", href: "/overview" },
 *     {
 *       label: "More",
 *       href: "#",
 *       menu: [
 *         { label: "Photos", href: "/photos" },
 *         { label: "Travel", href: "/travel" },
 *       ]
 *     },
 *   ]}
 * />
 */
const TabNav = forwardRef<'nav', TabNavProps>((props, ref) => {
  const {
    items,
    variant = 'default',
    indicatorStyle = 'underline',
    linkComponent: LinkComponent = DefaultLinkComponent,
    overrides = {},
    menuStyleOverrides,
    menuPopperOptions,
    menuUsePortal,
    onTabChange,
    'aria-label': ariaLabel,
    'data-testid': testId,
  } = props;

  const isNative = variant === 'native';
  const isPillStyle = indicatorStyle === 'pill';

  const { activeIndex, getItemProps, setItemRef, indicatorRef, listRef, toggleMenu, closeMenu, isMenuOpen } = useTabNav({
    items,
    variant,
    indicatorStyle,
    onTabChange,
  });

  const {
    Root: [Root, rootProps],
    List: [List, listProps],
    Item: [Item, itemProps],
    Link: [Link, linkProps],
    Indicator: [Indicator, indicatorProps],
    MenuTrigger: [MenuTrigger, menuTriggerProps],
    Badge: [Badge, badgeProps],
  } = useOverrides(
    {
      Root: StyledRoot,
      List: StyledList,
      Item: StyledItem,
      Link: StyledLink,
      Indicator: StyledIndicator,
      MenuTrigger: StyledMenuTrigger,
      Badge: StyledBadge,
    },
    overrides,
  );

  const styles = useStyleConfig(tabNavStyles, props);

  const popoverOverrides = useMemo(
    () => ({
      ...(menuStyleOverrides?.popoverContent && { Content: { props: menuStyleOverrides.popoverContent } }),
      Body: { props: { minWidth: pxToRem(280), padding: 2, ...menuStyleOverrides?.popoverBody } },
    }),
    [menuStyleOverrides?.popoverContent, menuStyleOverrides?.popoverBody],
  );

  const renderBadgeContent = (badge: TabNavItem['badge']) =>
    typeof badge === 'number' ? (
      <JoyBadge
        variant="counter"
        count={badge}
        size="sm"
        backgroundColor="mono14"
      />
    ) : (
      badge
    );

  const renderTabItem = (item: TabNavItem, index: number) => {
    const isActive = index === activeIndex;
    const { 'aria-current': ariaCurrent, tabIndex, onKeyDown } = getItemProps(index);
    const hasBadge = item.badge !== undefined && item.badge !== null;

    // Determine link styles based on active state, variant, and indicator style
    const activeLinkStyles = getActiveLinkStyles(isActive, isNative, isPillStyle);
    const inactiveLinkStyles = !isActive ? getInactiveNativeLinkStyles(isNative, isPillStyle) : {};

    // If item has a menu, render as menu trigger
    if (item.menu && item.menu.length > 0) {
      const menuIsOpen = isMenuOpen(index);

      return (
        <Item
          as="li"
          key={item.href}
          ref={setItemRef(index)}
          __css={styles.item}
          {...itemProps}
        >
          <PopoverV2
            placement="bottom"
            isOpen={menuIsOpen}
            onClose={() => closeMenu(index)}
            overrides={popoverOverrides}
            popperOptions={menuPopperOptions}
          >
            <PopoverV2.Trigger>
              <MenuTrigger
                as="button"
                type="button"
                aria-current={ariaCurrent}
                aria-expanded={menuIsOpen}
                aria-haspopup="true"
                aria-controls={`tabnav-menu-${index}`}
                tabIndex={tabIndex}
                onKeyDown={onKeyDown}
                onClick={() => toggleMenu(index)}
                __css={{
                  ...styles.menuTrigger,
                  ...activeLinkStyles,
                  ...inactiveLinkStyles,
                }}
                {...menuTriggerProps}
              >
                {item.label}
                {item.endAdornment}
                {hasBadge && (
                  <Badge
                    __css={styles.badge}
                    {...badgeProps}
                  >
                    {renderBadgeContent(item.badge)}
                  </Badge>
                )}
                <ExpandArrow $isOpen={menuIsOpen} />
              </MenuTrigger>
            </PopoverV2.Trigger>
            {(() => {
              const menuContent = (
                <PopoverV2.Content>
                  <Flex
                    id={`tabnav-menu-${index}`}
                    flexDirection="column"
                    role="menu"
                  >
                    {item.menu.map((menuItem, menuIndex) => (
                      <TabNavMenuItemComponent
                        key={`${menuItem.label}-${menuIndex}`}
                        item={menuItem}
                        onClose={() => closeMenu(index)}
                        linkComponent={LinkComponent}
                        menuStyleOverrides={menuStyleOverrides}
                      />
                    ))}
                  </Flex>
                </PopoverV2.Content>
              );
              return menuUsePortal ? <Portal>{menuContent}</Portal> : menuContent;
            })()}
          </PopoverV2>
        </Item>
      );
    }

    // Regular tab link
    return (
      <Item
        as="li"
        key={item.href}
        ref={setItemRef(index)}
        __css={styles.item}
        {...itemProps}
      >
        <Link
          as={LinkComponent as React.ElementType}
          href={item.href}
          aria-current={ariaCurrent}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown}
          __css={{
            ...styles.link,
            ...activeLinkStyles,
            ...inactiveLinkStyles,
            ...(hasBadge ? { gap: 2 } : {}),
          }}
          {...linkProps}
        >
          {item.label}
          {item.endAdornment}
          {hasBadge && (
            <Badge
              __css={styles.badge}
              {...badgeProps}
            >
              {renderBadgeContent(item.badge)}
            </Badge>
          )}
        </Link>
      </Item>
    );
  };

  return (
    <Root
      as="nav"
      ref={ref}
      aria-label={ariaLabel}
      data-testid={testId}
      __css={styles.root}
      {...rootProps}
    >
      <List
        as="ul"
        role="list"
        ref={listRef}
        __css={styles.list}
        {...listProps}
      >
        {/* Sliding indicator - rendered first for native/pill variants so it appears behind tabs */}
        {(isNative || isPillStyle) && (
          <Indicator
            ref={indicatorRef}
            aria-hidden="true"
            __css={styles.indicator}
            {...indicatorProps}
          />
        )}

        {items.map(renderTabItem)}

        {/* Sliding indicator - rendered after for underline variants (on bottom) */}
        {!isNative && !isPillStyle && (
          <Indicator
            ref={indicatorRef}
            aria-hidden="true"
            __css={styles.indicator}
            {...indicatorProps}
          />
        )}
      </List>
    </Root>
  );
});

TabNav.displayName = createDisplayName('TabNav');

export { TabNav };
